import * as React from 'react';

import {useQuery, useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {Modal} from 'shells/modal/modal';

import type {
    GetWebFormStarters as GetFormStartersQuery,
    GetWebFormStarters_webFormStarters as FormStarter,
    CreateForm as CreateFormMutation,
    CreateFormVariables as CreateFormMutationVariables,
} from 'nutshell-graphql-types';

import {appHistory} from '../../../history';

import {useGetLimitsByType, FeatureLimitsModalContent} from '../../limits';

import CREATE_FORM from '../graphql/mutations/create-form.graphql';
import GET_FORM_STARTERS_QUERY from '../graphql/queries/get-form-starters.graphql';

import {NewFormForm} from './new-form-form';
import './new-form-modal.module.css';

export const NEW_FORM_ID = 'CREATE_FROM_SCRATCH';

type OwnProps = {
    isOpen: boolean;
    onClose: () => void;
};

type Props = OwnProps & {
    formStarters: FormStarter[];
    isLoading: boolean;
    error?: string | null | undefined;
    onCreateForm: (name: string, starterId?: string) => Promise<any>;
};

function NewFormModalComponent(props: Props) {
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const {limits, isLoading: isLoadingLimits} = useGetLimitsByType('FORMS');

    const onCloseModal = () => {
        setIsSaving(false);
        props.onClose();
    };

    const onCreateForm = (name: string, starterId?: string) => {
        setIsSaving(true);

        return props.onCreateForm(name, starterId).then(() => {
            onCloseModal();
        });
    };

    return (
        <Modal
            trapFocus={true}
            isOpen={props.isOpen}
            headerText='Create a new form'
            onClose={onCloseModal}
            // These values fit the pre-made forms content perfectly
            maxHeight={limits && !limits.canAdd ? 575 : 554}
            minHeight={limits && !limits.canAdd ? 575 : 554}
        >
            {limits && !limits.canAdd ? (
                <FeatureLimitsModalContent onClose={onCloseModal} {...limits} />
            ) : (
                <NewFormForm
                    isLoading={props.isLoading || isLoadingLimits}
                    isSaving={isSaving}
                    onCreateForm={onCreateForm}
                    onCloseModal={onCloseModal}
                    error={props.error}
                    formStarters={props.formStarters}
                />
            )}
        </Modal>
    );
}

export function NewFormModal(props: OwnProps) {
    const {data, loading, error} = useQuery<GetFormStartersQuery, Record<any, any>>(
        GET_FORM_STARTERS_QUERY,
        {
            notifyOnNetworkStatusChange: true,
        }
    );
    const webFormStarters = data ? data.webFormStarters : null;

    const [createForm] = useMutation<CreateFormMutation, CreateFormMutationVariables>(CREATE_FORM);

    return (
        <NewFormModalComponent
            {...props}
            isLoading={loading}
            formStarters={webFormStarters || []}
            // @ts-expect-error
            error={safelyGetGraphQLErrorMessage(error)}
            onCreateForm={(newFormName, formStarterId) => {
                return createForm({
                    variables: {
                        input: {form: {name: newFormName, webFormStarterId: formStarterId}},
                    },
                }).then((mutationResults) => {
                    const createdForm =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.formCreate &&
                        mutationResults.data.formCreate.form;

                    if (createdForm) {
                        appHistory.push(createdForm.htmlUrlPath);
                    }
                });
            }}
        />
    );
}
